import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Film, Plus, Edit, Trash2, X, Loader2, Upload, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { caption: '', videoUrl: '', order: 0, thumbnailUrl: '', active: true };

const ReelsManagement = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/admin/reels`); setReels(res.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setFile(null); setPreview(null); setModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ caption: r.caption || '', videoUrl: r.videoUrl || '', order: r.order || 0, thumbnailUrl: r.thumbnail || '', active: r.active !== false }); setFile(null); setPreview(r.thumbnail || null); setModal(true); };
  const onFile = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } };

  const save = async () => {
    if (!form.videoUrl.trim()) return toast({ title: 'Video URL is required', variant: 'destructive' });
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('caption', form.caption); fd.append('videoUrl', form.videoUrl); fd.append('order', String(form.order));
      fd.append('thumbnailUrl', form.thumbnailUrl || '');
      if (file) fd.append('thumbnail', file);
      if (editing) { fd.append('active', String(form.active)); await axios.put(`${API}/admin/reels/${editing.id}`, fd); }
      else { await axios.post(`${API}/admin/reels`, fd); }
      toast({ title: editing ? 'Reel updated' : 'Reel added' });
      setModal(false); load();
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm('Delete this reel?')) return;
    try { await axios.delete(`${API}/admin/reels/${r.id}`); toast({ title: 'Deleted' }); load(); }
    catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Film className="w-6 h-6 text-blue-600" /> Landing Reels</h2>
        <Button onClick={openNew} data-testid="add-reel-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-2" /> Add Reel</Button>
      </div>
      <p className="text-sm text-slate-500">These short reels auto-scroll (right \u2192 left) in a carousel above the &quot;Why Choose Us&quot; section. Paste a YouTube/Shorts/Instagram link or an mp4 URL, and add a vertical thumbnail image.</p>

      {loading ? (
        <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {reels.length === 0 && <div className="text-slate-400 text-sm">No reels yet.</div>}
          {reels.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden relative group">
              <div className="aspect-[9/16] bg-slate-100 relative">
                {r.thumbnail ? <img src={r.thumbnail} alt={r.caption} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Play className="w-8 h-8 text-slate-400" /></div>}
                {r.active === false && <span className="absolute top-1 left-1 text-[9px] bg-red-500 text-white px-1.5 py-0.5 rounded">Hidden</span>}
              </div>
              <div className="p-2">
                <p className="text-xs text-slate-700 line-clamp-2 min-h-[2em]">{r.caption || 'Reel'}</p>
              </div>
              <div className="flex gap-1 absolute top-1 right-1">
                <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg bg-white/90 hover:bg-white text-slate-600 shadow"><Edit className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(r)} className="p-1.5 rounded-lg bg-white/90 hover:bg-white text-red-500 shadow"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setModal(false)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            <h3 className="text-lg font-bold text-slate-800 mb-4">{editing ? 'Edit Reel' : 'Add Reel'}</h3>
            <div className="space-y-3">
              <input value={form.videoUrl} onChange={(e) => setForm({ ...form, videoUrl: e.target.value })} placeholder="Video URL (YouTube/Shorts/mp4) *" data-testid="reel-url" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              <input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Caption" data-testid="reel-caption" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              <div>
                <label className="text-xs text-slate-500 block mb-1">Thumbnail image (vertical 9:16 looks best)</label>
                <label className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-3 cursor-pointer hover:border-blue-400">
                  <Upload className="w-4 h-4 text-slate-400" /><span className="text-sm text-slate-500">{file ? file.name : 'Upload thumbnail (or paste URL)'}</span>
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="reel-thumb" />
                </label>
                <input value={form.thumbnailUrl} onChange={(e) => { setForm({ ...form, thumbnailUrl: e.target.value }); setPreview(e.target.value); }} placeholder="or thumbnail URL" className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm" />
                {preview && <img src={preview} alt="preview" className="mt-2 w-20 aspect-[9/16] object-cover rounded-lg border" />}
              </div>
              <div>
                <label className="text-xs text-slate-500">Sort order</label>
                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-blue-600" /> Visible on site</label>
              )}
              <Button onClick={save} disabled={saving} data-testid="reel-save" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Reel'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelsManagement;
